/**
 * Árbol de rutas.
 *
 * Cada página se carga con `lazy` para que el bundle inicial solo contenga lo
 * necesario para la pantalla de entrada; el panel de administración, que la
 * mayoría de usuarios nunca abre, no se descarga hasta que hace falta.
 */

import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { PageLoader } from '@/components/ui';
import { RedirectIfAuthenticated, RequireAdmin, RequireAuth } from '@/routes/guards';
import { useAuthStore } from '@/stores/authStore';

const LandingPage = lazy(() => import('@/features/auth/LandingPage'));
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'));
const ProfilePage = lazy(() => import('@/features/auth/ProfilePage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const GamePage = lazy(() => import('@/features/game/GamePage'));
const GameResultPage = lazy(() => import('@/features/game/GameResultPage'));
const LeaderboardPage = lazy(() => import('@/features/leaderboard/LeaderboardPage'));
const StatsPage = lazy(() => import('@/features/stats/StatsPage'));
const AdminDashboardPage = lazy(() => import('@/features/admin/AdminDashboardPage'));
const AdminQuestionsPage = lazy(() => import('@/features/admin/AdminQuestionsPage'));
const AdminQuestionFormPage = lazy(() => import('@/features/admin/AdminQuestionFormPage'));
const AdminLevelsPage = lazy(() => import('@/features/admin/AdminLevelsPage'));
const AdminUsersPage = lazy(() => import('@/features/admin/AdminUsersPage'));
const NotFoundPage = lazy(() => import('@/features/auth/NotFoundPage'));

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    // Un único intento de restaurar la sesión al cargar la app.
    void bootstrap();
  }, [bootstrap]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<RedirectIfAuthenticated />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/ingresar" element={<LoginPage />} />
          <Route path="/registro" element={<RegisterPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/jugar" element={<GamePage />} />
            <Route path="/jugar/:sessionId/resultado" element={<GameResultPage />} />
            <Route path="/ranking" element={<LeaderboardPage />} />
            <Route path="/estadisticas" element={<StatsPage />} />
            <Route path="/perfil" element={<ProfilePage />} />

            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/preguntas" element={<AdminQuestionsPage />} />
              <Route path="/admin/preguntas/nueva" element={<AdminQuestionFormPage />} />
              <Route path="/admin/preguntas/:questionId" element={<AdminQuestionFormPage />} />
              <Route path="/admin/niveles" element={<AdminLevelsPage />} />
              <Route path="/admin/usuarios" element={<AdminUsersPage />} />
            </Route>
          </Route>
        </Route>

        {/* Rutas de la app Flask que la gente pueda tener guardadas. */}
        <Route path="/login" element={<Navigate to="/ingresar" replace />} />
        <Route path="/register" element={<Navigate to="/registro" replace />} />
        <Route path="/game/*" element={<Navigate to="/jugar" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
